import { useCallback, useEffect, useState } from 'react';
import {
  onboardingStateSchema,
  type OnboardingEvent,
  type OnboardingState,
} from '@forecastify/shared';
import { useSession } from '../session';

export interface Onboarding {
  /** `null` until the first read answers — and stays `null` if it never does. */
  readonly state: OnboardingState | null;
  /** Record what happened. The screen moves on at once; the server's answer replaces the guess. */
  readonly record: (event: OnboardingEvent) => void;
}

/**
 * F-1 · whether this person has been welcomed, and what they did with the tour.
 *
 * ⚠️⚠️ **A FAILED READ SHOWS NOTHING, RATHER THAN THE WELCOME.** `null` is *"not answered"*, and the
 * cards open only on an answer that says `welcomeSeen: false`. Treating a network error as *never
 * welcomed* would greet an established team on every blip — the one failure this must not have.
 */
export function useOnboarding(): Onboarding {
  const session = useSession();
  const [state, setState] = useState<OnboardingState | null>(null);

  useEffect(() => {
    /* A property rather than a `let` — see `permissions-hook.ts` for why the narrowing matters. */
    const alive = { current: true };
    void (async () => {
      if (session.workspaceId === null) return;
      const response = await session.request('/account/onboarding').catch(() => null);
      if (response?.ok !== true) return;
      const parsed = onboardingStateSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success && alive.current) setState(parsed.data);
    })();
    return () => {
      alive.current = false;
    };
  }, [session]);

  const record = useCallback(
    (event: OnboardingEvent) => {
      setState((current) =>
        current === null
          ? current
          : {
              welcomeSeen: current.welcomeSeen || event === 'welcome_seen',
              tourFinished: current.tourFinished || event === 'tour_finished',
              tourSkipped: current.tourSkipped || event === 'tour_skipped',
            },
      );
      void (async () => {
        const response = await session
          .request('/account/onboarding', { method: 'POST', body: JSON.stringify({ event }) })
          .catch(() => null);
        if (response?.ok !== true) return;
        const parsed = onboardingStateSchema.safeParse(await response.json().catch(() => null));
        if (parsed.success) setState(parsed.data);
      })();
    },
    [session],
  );

  return { state, record };
}
